import type {
  LabMarkerQuestionCandidateAnswerT,
  LabTestQuestionBodyTValidator,
  LabTestQuestionT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { Input } from '@/shared/ui/shadcn/input';
import { Label } from '@/shared/ui/shadcn/label';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/shadcn/select';

function normalizeCandidates(
  answers: LabMarkerQuestionCandidateAnswerT[]
): LabMarkerQuestionCandidateAnswerT[] {
  return answers.filter((a) => a.code.trim() !== '');
}

function isChoiceLike(
  validator: LabTestQuestionBodyTValidator
): validator is { type: 'choice' | 'multi_choice'; answers: LabMarkerQuestionCandidateAnswerT[] } {
  return validator.type === 'choice' || validator.type === 'multi_choice';
}

interface AoeQuestionInputProps {
  question: LabTestQuestionT;
  value: string;
  onChange: (value: string) => void;
}

export function AoeQuestionInput({ question, value, onChange }: AoeQuestionInputProps) {
  const { body } = question;
  const { validator } = body;
  const required = body.required;

  const label = body.value;
  const helpText = body.constraint ?? null;

  const requiredIndicator = required ? (
    <span className="ml-1 text-xs font-medium text-destructive">*</span>
  ) : null;

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <Label className="text-sm">
          {label}
          {requiredIndicator}
        </Label>
        {helpText ? <div className="text-xs text-muted-foreground">{helpText}</div> : null}
      </div>

      {isChoiceLike(validator) ? (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select an answer…" />
          </SelectTrigger>
          <SelectContent>
            {normalizeCandidates(validator.answers).map((a) => (
              <SelectItem key={a.code} value={a.code}>
                {a.value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : validator.type === 'numeric' ? (
        <Input type="number" value={value} onChange={(e) => onChange(e.target.value)} />
      ) : validator.type === 'text' ? (
        <Textarea value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <Input value={value} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}
